import { observer } from 'mobx-react';
import { FC, useState, useEffect } from 'react';
import routes from '../../../modules/routes/index.routes';
import { Page, Pagebar, PagebarTitle, PagebarActions } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useList } from '../../../modules/hooks/tools/use-list';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { CreativeModel } from '../../../modules/models/creative.model';
import { useCreativeActions } from '../../../modules/hooks/actions/creatives.actions';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import { getFilterObject, PerformancesColumnsSnakeCase } from '../../../modules/hooks/apis/use-reports-list';
import { useMergeWithPerformance } from '../../../modules/hooks/tools/use-merge-with-performance';
import { usePerformanceEntity } from '../../../modules/hooks/apis/use-performance-entity';
import { useCreativeHeaders } from '../../../modules/hooks/theaders/creatives.headers';
import { IAdminxStore } from '../../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useRoute } from '../../../modules/hooks/tools/use-route';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { queryParamsToSearchParams } from '@monorepo/tools/src/lib/utils/url';
import styles from './creatives.module.scss';

const pageBarDebugProps = { dataAttrs: [new DataAttribute('id', 'creatives_pagebar')] };
const tableDebugProps = { dataAttrs: [new DataAttribute('id', 'creatives_table')] };

export const Creatives: FC = observer(() => {
	const { creativeStore } = useStores<IAdminxStore>();
	const { SelectedActions, FiltersActions, SegmentActions, EntityActions, IndexActions } = useCreativeActions();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const [showLinkedCreatives, setShowLinkedCreatives] = useState<boolean>(false);

	const performanceStore = creativeStore.getPerformance();

	useList<CreativeModel>(creativeStore.getListStore());

	useEffect(() => {
		const params = queryParamsToSearchParams(showLinkedCreatives ? { linkedToCampaign: true } : {});
		creativeStore.getListStore().reset();
		creativeStore.getListStore().fetch(params);
	}, [showLinkedCreatives, creativeStore]);

	const currentFilters = usePageSettings()?.tableStore?.getFiltersStore(currentRouteWithoutSpecialChars)?.getCurrentFilters();

	const performanceFilters = getFilterObject(currentFilters || new Map(), 'adxCreatives');

	if (showLinkedCreatives) {
		performanceFilters.push({
			column: 'is_linked',
			filterType: 'EQUALS',
			value: ['true'],
			inverted: false,
		});
	}

	usePerformanceEntity(performanceStore, {
		limit: 1000,
		reportName: 'adxCreatives',
		filters: performanceFilters,
	});

	const { data, summary } = useMergeWithPerformance<CreativeModel>({
		performanceStore,
		data: creativeStore.getListStore().getList(),
		key: PerformancesColumnsSnakeCase.CreativeId,
		attachExtraParams(row) {
			return {
				brand: row.brand,
				adx_creative_id: row.adx_creative_id,
				name: row.brand,
				description: row.description,
				resource_url: row.resource_url,
				resource_type: row.resource_type,
				adx_network_status: row.adx_network_status,
				adx_deals_status: row.adx_deals_status,
				size: row.size,
				default_dest_url: row.default_dest_url,
				creative_group: row.creative_group,
				status: row.status,
			};
		},
	});

	const { columns } = useCreativeHeaders({ EntityActions, summary });

	return (
		<Page>
			<Pagebar debugProps={pageBarDebugProps}>
				<PagebarTitle>Creatives</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
				</PagebarActions>
			</Pagebar>
			<ControlledTable
				currentRoute={currentRouteWithoutSpecialChars}
				createRoute={routes.creatives.create()}
				creativesLinkedToggle={
					<Toggle
						classNames={styles.toggleTitle}
						label={showLinkedCreatives ? 'On' : 'Off'}
						title="Linked Creatives"
						onChange={setShowLinkedCreatives}
						defaultValue={showLinkedCreatives}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'linked_creatives_toggle')] }}
					/>
				}
				columns={columns}
				debugProps={tableDebugProps}
				data={data}
				isLoading={creativeStore.getListStore().getIsLoading() || performanceStore.getIsLoading()}
				isSummary>
				{() => {
					return {
						IndexActions,
						FiltersActions,
						SelectedActions,
						SegmentActions,
					};
				}}
			</ControlledTable>
		</Page>
	);
});
